import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { purchaseApi, inventoryApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * Purchase orders — FR-PUR-01.01–05
 * Lifecycle: draft → pending_approval → approved → partially_received → fully_received
 *
 * Two-pane layout (list on left, detail on right) with KPI strip and
 * lifecycle status chips. Status uses explicit tone mapping because
 * pending_approval should visibly differ from draft.
 */
export function PurchaseOrdersPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const listQuery = useQuery({
    queryKey: ['purchase', 'orders'],
    queryFn: () => purchaseApi.listOrders({ limit: 500 }),
  });
  const deletePoMut = useMutation({
    mutationFn: (id: string) => purchaseApi.deletePo(id),
    onSuccess: (r: any) => { toast.success(r?.message || 'Draft purchase order deleted'); qc.invalidateQueries({ queryKey: ['purchase'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { kpis, filteredRows } = useMemo(() => {
    const rows = (listQuery.data ?? []) as any[];
    const k = {
      total: rows.length,
      pendingApproval: rows.filter((o) => o.status === 'pending_approval').length,
      approved: rows.filter((o) => o.status === 'approved').length,
      received: rows.filter((o) => ['partially_received', 'fully_received'].includes(o.status)).length,
    };
    const filtered = statusFilter
      ? rows.filter((o) => o.status === statusFilter)
      : rows;
    return { kpis: k, filteredRows: filtered };
  }, [listQuery.data, statusFilter]);

  const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '',                    label: 'All' },
    { value: 'draft',               label: 'Draft' },
    { value: 'pending_approval',    label: 'Pending approval' },
    { value: 'approved',            label: 'Approved' },
    { value: 'partially_received',  label: 'Partially received' },
    { value: 'fully_received',      label: 'Fully received' },
    { value: 'cancelled',           label: 'Cancelled' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Purchasing"
        title="Purchase orders"
        subtitle="Create, submit, and track POs. Auto-approval by threshold, full audit trail on every action."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <PlusIcon /> New PO
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total POs" value={kpis.total} />
        <KpiTile label="Pending approval" value={kpis.pendingApproval}
          accent={kpis.pendingApproval > 0 ? 'amber' : 'default'} />
        <KpiTile label="Approved" value={kpis.approved} accent="sage" />
        <KpiTile label="Received" value={kpis.received} accent="sage" />
      </div>

      {/* Status filter chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Two-pane layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_28rem] gap-6">
        <div className="card overflow-hidden">
          {listQuery.isLoading ? (
            <LoadingState rows={6} />
          ) : listQuery.error ? (
            <ErrorState error={listQuery.error} />
          ) : !filteredRows.length ? (
            <EmptyState
              message="No purchase orders"
              hint={statusFilter
                ? `No POs in "${statusFilter.replace('_', ' ')}" status.`
                : 'Create your first PO to see it here.'}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>PO</th>
                    <th>Supplier</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="text-right">Total</th>
                    <th className="text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((po: any) => (
                    <tr
                      key={po.id}
                      onClick={() => setSelected(po.id)}
                      className={clsx(
                        'cursor-pointer',
                        selected === po.id && '!bg-canvas-subtle'
                      )}
                    >
                      <td className="font-mono text-xs text-ink-900 font-semibold">
                        {po.po_number}
                      </td>
                      <td className="text-sm text-ink-700 truncate max-w-[12rem]">
                        {po.supplier_name}
                      </td>
                      <td className="text-sm text-ink-500">
                        {new Date(po.order_date).toLocaleDateString(undefined, {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </td>
                      <td><PoStatusBadge status={po.status} /></td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                        ${parseFloat(po.grand_total).toFixed(2)}
                      </td>
                      <td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                        {po.status === 'draft' && (
                          <button
                            className="text-xs font-medium px-2 py-1 rounded transition-colors text-red-600 hover:bg-red-50"
                            disabled={deletePoMut.isPending}
                            onClick={() => { if (window.confirm(`Delete draft purchase order ${po.po_number}?`)) deletePoMut.mutate(po.id); }}>
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select a purchase order"
              hint="Click a row on the left to view PO details and approval actions."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              }
            />
          ) : (
            <PoDetail
              poId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['purchase', 'orders'] })}
            />
          )}
        </div>
      </div>

      {showCreate && (
        <CreatePoModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['purchase', 'orders'] });
            setShowCreate(false);
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// PoStatusBadge — explicit tone mapping for PO lifecycle
// ============================================================
function PoStatusBadge({ status }: { status: string }) {
  // Map PO lifecycle statuses to our semantic tones.
  // Shared StatusBadge's inferTone doesn't know these specific values,
  // so we pass an explicit tone.
  const tone =
    status === 'draft'                ? 'neutral' :
    status === 'pending_approval'     ? 'warning' :
    status === 'approved'             ? 'sage' :
    status === 'partially_received'   ? 'info' :
    status === 'fully_received'       ? 'sage' :
    status === 'billed'               ? 'info' :
    status === 'paid'                 ? 'sage' :
    status === 'cancelled'            ? 'danger' :
                                        'neutral';
  return <StatusBadge status={status.replace('_', ' ')} tone={tone as any} />;
}

// ============================================================
// PoDetail — workflow panel
// ============================================================
function PoDetail({ poId, onChanged }: { poId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ['purchase', 'order', poId],
    queryFn: () => purchaseApi.getOrder(poId),
  });

  const [showCancel, setShowCancel] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const submitMut = useMutation({
    mutationFn: () => purchaseApi.submitOrder(poId),
    onSuccess: (data) => {
      if (data.autoApproved) {
        toast.success(`PO ${data.poNumber} auto-approved (under $${data.threshold} threshold)`);
      } else {
        toast.success(`PO ${data.poNumber} submitted for approval (${fmtCurrency(data.grandTotal)} > $${data.threshold})`, { duration: 5000 });
      }
      qc.invalidateQueries({ queryKey: ['purchase', 'order', poId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const decideMut = useMutation({
    mutationFn: ({ approve, reason }: { approve: boolean; reason?: string }) =>
      purchaseApi.decideOrder(poId, approve, reason),
    onSuccess: (data) => {
      toast.success(data.rejected ? `PO rejected, back to draft` : `PO ${data.poNumber} approved`);
      qc.invalidateQueries({ queryKey: ['purchase', 'order', poId] });
      onChanged();
      setShowReject(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const cancelMut = useMutation({
    mutationFn: (reason: string) => purchaseApi.cancelOrder(poId, reason),
    onSuccess: () => {
      toast.success('PO cancelled');
      qc.invalidateQueries({ queryKey: ['purchase', 'order', poId] });
      onChanged();
      setShowCancel(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (detailQuery.isLoading) return <LoadingState rows={4} />;
  if (detailQuery.error) return <ErrorState error={detailQuery.error} />;
  const po = detailQuery.data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{po.po_number}</div>
          <div className="font-display font-bold text-lg text-ink-900 truncate">{po.supplier_name}</div>
          <div className="font-mono text-xs text-ink-500">{po.supplier_code}</div>
          {po.warehouse_code && (
            <div className="text-xs text-ink-500 mt-1">
              → {po.warehouse_code} {po.warehouse_name}
            </div>
          )}
          {po.notes && (
            <div className="text-xs text-ink-500 italic mt-1">{po.notes}</div>
          )}
        </div>
        <PoStatusBadge status={po.status} />
      </div>

      {/* Timeline */}
      <div className="bg-canvas-subtle rounded-xl p-3 mb-4 space-y-1.5 text-xs text-ink-600">
        <div>
          <span className="text-ink-400">Created:</span>{' '}
          {new Date(po.created_at).toLocaleString()} · {po.created_by_name}
        </div>
        {po.required_date && (
          <div>
            <span className="text-ink-400">Required by:</span>{' '}
            {new Date(po.required_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        )}
        {po.submitted_at && (
          <div>
            <span className="text-ink-400">Submitted:</span>{' '}
            {new Date(po.submitted_at).toLocaleString()} · {po.submitted_by_name}
          </div>
        )}
        {po.approved_at && (
          <div className="text-sage-700">
            <span className="text-sage-600">Approved:</span>{' '}
            {new Date(po.approved_at).toLocaleString()} · {po.approved_by_name}
          </div>
        )}
      </div>

      {/* Rejection / cancellation alerts */}
      {po.rejected_at && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 mb-3">
          <div className="font-semibold">Rejected</div>
          <div className="mt-0.5">
            {new Date(po.rejected_at).toLocaleString()} · {po.rejected_by_name}
          </div>
          {po.rejection_reason && <div className="mt-1">{po.rejection_reason}</div>}
        </div>
      )}
      {po.cancelled_at && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 mb-3">
          <div className="font-semibold">Cancelled</div>
          <div className="mt-0.5">
            {new Date(po.cancelled_at).toLocaleString()} · {po.cancelled_by_name}
          </div>
          {po.cancellation_reason && <div className="mt-1">{po.cancellation_reason}</div>}
        </div>
      )}

      {/* Line items */}
      <div className="border border-ink-100 rounded-xl overflow-hidden mb-4">
        <table className="table text-xs">
          <thead>
            <tr>
              <th>Item</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Unit</th>
              <th className="text-right">Tax %</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {po.lines.map((l: any) => (
              <tr key={l.id}>
                <td>
                  <div className="font-medium text-ink-900">{l.product_name}</div>
                  <div className="font-mono text-2xs text-ink-500">
                    {l.sku}{l.variant_sku ? ` / ${l.variant_sku}` : ''}
                  </div>
                  {l.description && l.description !== l.product_name && (
                    <div className="text-2xs text-ink-500 mt-0.5">{l.description}</div>
                  )}
                </td>
                <td className="text-right font-mono tabular-nums">{parseFloat(l.quantity).toFixed(0)}</td>
                <td className="text-right font-mono tabular-nums">{fmtCurrency(parseFloat(l.unit_price))}</td>
                <td className="text-right font-mono text-ink-500 tabular-nums">{parseFloat(l.tax_rate).toFixed(1)}%</td>
                <td className="text-right font-mono font-semibold tabular-nums">
                  ${parseFloat(l.line_total).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="bg-canvas-subtle rounded-xl p-4 mb-4">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-ink-600">
            <span>Subtotal</span>
            <span className="font-mono tabular-nums">{fmtCurrency(parseFloat(po.subtotal))}</span>
          </div>
          <div className="flex justify-between text-ink-600">
            <span>Tax</span>
            <span className="font-mono tabular-nums">{fmtCurrency(parseFloat(po.tax_total))}</span>
          </div>
        </div>
        <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-ink-200">
          <span className="font-semibold text-ink-900">Grand total</span>
          <span className="font-display font-bold text-2xl text-ink-900 tabular-nums tracking-tight">
            ${parseFloat(po.grand_total).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Workflow actions */}
      <div className="flex gap-2 justify-end flex-wrap">
        {po.status === 'draft' && (
          <>
            <button
              className="btn-secondary"
              disabled={cancelMut.isPending}
              onClick={() => setShowCancel(true)}
            >Cancel PO</button>
            <button
              className="btn-primary"
              disabled={submitMut.isPending}
              onClick={() => submitMut.mutate()}
            >
              {submitMut.isPending ? 'Submitting…' : 'Submit for approval'}
            </button>
          </>
        )}
        {po.status === 'pending_approval' && (
          <>
            <button
              className="btn-secondary"
              disabled={decideMut.isPending}
              onClick={() => setShowReject(true)}
            >Reject</button>
            <button
              className="btn-primary"
              disabled={decideMut.isPending}
              onClick={() => decideMut.mutate({ approve: true })}
            >
              {decideMut.isPending ? 'Approving…' : 'Approve'}
            </button>
          </>
        )}
        {po.status === 'approved' && (
          <button
            className="btn-secondary"
            disabled={cancelMut.isPending}
            onClick={() => setShowCancel(true)}
          >Cancel PO</button>
        )}
      </div>

      {showCancel && (
        <CancelPoDialog
          poNumber={po.po_number}
          loading={cancelMut.isPending}
          onClose={() => setShowCancel(false)}
          onConfirm={(reason) => cancelMut.mutate(reason)}
        />
      )}
      {showReject && (
        <RejectPoDialog
          poNumber={po.po_number}
          loading={decideMut.isPending}
          onClose={() => setShowReject(false)}
          onConfirm={(reason) => decideMut.mutate({ approve: false, reason })}
        />
      )}
    </div>
  );
}

// ============================================================
// PO workflow dialogs
// ============================================================
function CancelPoDialog({
  poNumber, loading, onClose, onConfirm,
}: { poNumber: string; loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Purchase · Cancel</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Cancel purchase order?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Cancel <span className="font-mono font-semibold text-ink-900">{poNumber}</span>?
          Cancellation is final — the PO stays on record for audit but cannot be re-opened.
          A fresh PO would need to be drafted to place this order again.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="text-danger">*</span></label>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Supplier can no longer fulfil"
        />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Keep PO</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason.trim())}
        >
          {loading ? 'Cancelling…' : 'Cancel PO'}
        </button>
      </div>
    </Modal>
  );
}

function RejectPoDialog({
  poNumber, loading, onClose, onConfirm,
}: { poNumber: string; loading: boolean; onClose: () => void; onConfirm: (reason: string | undefined) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Purchase · Reject</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Reject approval?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Reject <span className="font-mono font-semibold text-ink-900">{poNumber}</span>?
          The PO moves back to draft so the buyer can revise or cancel it. Your reason is
          visible to the submitter.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Budget not yet allocated for this quarter"
        />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={() => onConfirm(reason.trim() || undefined)}
        >
          {loading ? 'Rejecting…' : 'Reject PO'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// KpiTile
// ============================================================
function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

// ============================================================
// CreatePoModal
// ============================================================
function CreatePoModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [requiredDate, setRequiredDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Array<{
    productId: string; productName?: string; sku?: string;
    description: string; quantity: number; unitPrice: number; taxRate: number;
  }>>([]);
  const [productSearch, setProductSearch] = useState('');

  const supQuery = useQuery({ queryKey: ['suppliers'], queryFn: () => purchaseApi.listSuppliers() });
  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });
  const prodQuery = useQuery({
    queryKey: ['products-search', productSearch],
    queryFn: () => inventoryApi.listProducts({ q: productSearch, pageSize: 10 }),
    enabled: productSearch.length >= 2,
  });

  const createMut = useMutation({
    mutationFn: () => purchaseApi.createOrder({
      supplierId, warehouseId,
      requiredDate: requiredDate || undefined,
      notes: notes || undefined,
      lines: lines.map((l) => ({
        productId: l.productId,
        description: l.description,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        taxRate: l.taxRate,
      })),
    }),
    onSuccess: (data) => {
      toast.success(`PO ${data.poNumber} created as draft. Submit when ready.`);
      onCreated(data.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = supplierId && warehouseId && lines.length > 0
    && lines.every((l) => l.quantity > 0 && l.unitPrice >= 0);

  const subtotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0);
  const taxTotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice * (l.taxRate / 100), 0);
  const grandTotal = subtotal + taxTotal;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <div className="eyebrow mb-1">Purchasing</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New purchase order</h2>
      </div>

      <div className="px-6 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3 flex-shrink-0">
        <div>
          <label className="label">Supplier</label>
          <select className="input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">Select supplier…</option>
            {supQuery.data?.map((s: any) => (
              <option key={s.id} value={s.id}>{s.code} — {s.legal_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Deliver to</label>
          <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">Select warehouse…</option>
            {whQuery.data?.map((w: any) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Required by</label>
          <input type="date" className="input" value={requiredDate}
            onChange={(e) => setRequiredDate(e.target.value)} />
        </div>
      </div>

      <div className="px-6 pb-3">
        <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal memo, delivery instructions…" />
      </div>

      <div className="px-6 flex-1 overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <div className="eyebrow text-ink-500">Line items ({lines.length})</div>
        </div>
        <div className="border border-ink-100 rounded-xl overflow-hidden">
          <div className="p-3 border-b border-ink-100 relative">
            <div className="relative">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="input pl-9 text-sm"
                placeholder="Search product to add…"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
            </div>
            {prodQuery.data?.data?.length > 0 && productSearch.length >= 2 && (
              <div className="absolute left-3 right-3 top-full mt-1 bg-canvas-elevated border border-ink-200 rounded-xl shadow-lg max-h-60 overflow-y-auto z-20">
                {(prodQuery.data?.data ?? []).map((p: any) => (
                  <button
                    key={p.id}
                    className="w-full text-left px-3 py-2 hover:bg-canvas-subtle border-b border-ink-100 last:border-0 transition-colors"
                    onClick={() => {
                      setLines((prev) => [...prev, {
                        productId: p.id, productName: p.name, sku: p.sku,
                        description: p.name,
                        quantity: 1,
                        unitPrice: parseFloat(p.cost_price) || 0,
                        taxRate: 0,
                      }]);
                      setProductSearch('');
                    }}
                  >
                    <div className="font-semibold text-ink-900 text-sm">{p.name}</div>
                    <div className="font-mono text-xs text-ink-500">{p.sku}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {lines.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-500">
              Search above to add products to this PO.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Description</th>
                  <th className="text-right w-20">Qty</th>
                  <th className="text-right w-24">Unit</th>
                  <th className="text-right w-16">Tax %</th>
                  <th className="text-right w-24">Total</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const total = l.quantity * l.unitPrice * (1 + l.taxRate / 100);
                  return (
                    <tr key={i}>
                      <td className="text-xs">
                        <div className="font-mono text-2xs text-ink-500">{l.sku}</div>
                        <div className="text-ink-900">{l.productName}</div>
                      </td>
                      <td>
                        <input
                          className="input text-xs w-48"
                          value={l.description}
                          onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                        />
                      </td>
                      <td className="text-right">
                        <input
                          type="number" min="0.01" step="1"
                          className="input w-20 text-right tabular-nums"
                          value={l.quantity}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || 0;
                            setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: v } : x));
                          }}
                        />
                      </td>
                      <td className="text-right">
                        <input
                          type="number" min="0" step="0.01"
                          className="input w-24 text-right tabular-nums"
                          value={l.unitPrice}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || 0;
                            setLines((prev) => prev.map((x, j) => j === i ? { ...x, unitPrice: v } : x));
                          }}
                        />
                      </td>
                      <td className="text-right">
                        <input
                          type="number" min="0" max="100" step="0.1"
                          className="input w-16 text-right tabular-nums"
                          value={l.taxRate}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || 0;
                            setLines((prev) => prev.map((x, j) => j === i ? { ...x, taxRate: v } : x));
                          }}
                        />
                      </td>
                      <td className="text-right font-mono font-semibold tabular-nums">
                        ${total.toFixed(2)}
                      </td>
                      <td>
                        <button
                          className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                          onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                          aria-label="Remove line"
                        >✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex-shrink-0 flex items-center justify-between gap-3">
        <div>
          <div className="eyebrow text-ink-500">Total</div>
          <div className="font-display font-bold text-2xl text-ink-900 tabular-nums tracking-tight">
            ${grandTotal.toFixed(2)}
          </div>
          <div className="text-2xs text-ink-500 mt-0.5 font-mono">
            Subtotal ${subtotal.toFixed(2)} + Tax ${taxTotal.toFixed(2)}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!canSubmit || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// Icons
// ============================================================
const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function PlusIcon() { return <svg {...iconProps}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
